/* eslint-disable */
import React, { useState, useEffect, useCallback } from "react";
import {
  Table, Tag, Button, Select, Modal, Input,
  message, Tooltip, Descriptions,
} from "antd";
import Header from "../layouts/Header";
import Sidebar from "../layouts/Sidebar";
import { requestsApi } from "../../apis";

const statusColor = {
  pending: "gold",
  approved: "green",
  rejected: "red",
  awaiting_payment: "orange",
  completed: "green",
};

const money = (v) => (v == null ? "—" : `${Number(v).toLocaleString()} EGP`);

const fmtDate = (v) =>
  v
    ? new Date(v + "T00:00:00").toLocaleDateString("en-GB", {
        day: "numeric", month: "short", year: "numeric",
      })
    : "—";

const customerOf = (r) => {
  const res = r.reservation || {};
  if (res.user)
    return (
      <div>
        <div style={{ fontWeight: 600 }}>
          {res.user.firstName} {res.user.lastName}
        </div>
        <div style={{ fontSize: 12, color: "#6b7280" }}>{res.user.email}</div>
      </div>
    );
  return res.guestName || res.guestEmail || "—";
};

// Human-readable summary of what a change request wants
const changeLines = (r) => {
  if (r.type !== "change" || !r.requestedChanges) return [];
  const ch = r.requestedChanges;
  const res = r.reservation || {};
  const lines = [];
  if (ch.adultSeats != null && Number(ch.adultSeats) !== Number(res.adultSeats))
    lines.push(`Adults: ${res.adultSeats} → ${ch.adultSeats}`);
  if (ch.childSeats != null && Number(ch.childSeats) !== Number(res.childSeats))
    lines.push(`Children: ${res.childSeats} → ${ch.childSeats}`);
  if (ch.deckId && ch.deckId !== res.deckId)
    lines.push(`Deck: ${res.deck?.name || "current"} → new deck`);
  if (ch.tripDateId && ch.tripDateId !== res.tripDateId)
    lines.push("Date: new date requested");
  return lines;
};

const expandedRowRender = (r) => {
  const res = r.reservation || {};
  const trip = res.tripDate?.trip;
  const ch = r.requestedChanges || {};
  const lines = changeLines(r);

  return (
    <div style={{ padding: "12px 16px 16px", background: "#f9fafb" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 24,
          alignItems: "start",
        }}
      >
        {/* ── Current booking ── */}
        <div>
          <div
            style={{
              fontWeight: 700, fontSize: 12, color: "#6b7280",
              textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8,
            }}
          >
            Current Booking
          </div>
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label="Booking ID">
              <span style={{ fontFamily: "monospace" }}>
                #{(r.reservationId || "").slice(0, 8).toUpperCase()}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Trip">{trip?.name || "—"}</Descriptions.Item>
            <Descriptions.Item label="Date">{fmtDate(res.tripDate?.date)}</Descriptions.Item>
            <Descriptions.Item label="Deck">{res.deck?.name || "—"}</Descriptions.Item>
            <Descriptions.Item label="Seats">
              {res.adultSeats} adult{res.adultSeats !== 1 ? "s" : ""}
              {res.childSeats > 0
                ? `, ${res.childSeats} child${res.childSeats !== 1 ? "ren" : ""}`
                : ""}
            </Descriptions.Item>
            <Descriptions.Item label="Booking total">
              {money(res.totalPrice)}
            </Descriptions.Item>
          </Descriptions>
        </div>

        {/* ── What they want ── */}
        <div>
          <div
            style={{
              fontWeight: 700, fontSize: 12, color: "#6b7280",
              textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8,
            }}
          >
            {r.type === "cancel" ? "Cancellation Request" : "Requested Changes"}
          </div>
          <Descriptions size="small" column={1} bordered>
            {r.type === "cancel" ? (
              <>
                <Descriptions.Item label="Action">
                  <Tag color="red">Cancel booking</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Refund to wallet">
                  <strong>{money(r.computedRefund)}</strong>
                </Descriptions.Item>
              </>
            ) : (
              <>
                {lines.length ? (
                  lines.map((l, i) => (
                    <Descriptions.Item key={i} label={l.split(":")[0]}>
                      {l.split(":").slice(1).join(":").trim()}
                    </Descriptions.Item>
                  ))
                ) : (
                  <Descriptions.Item label="Changes">
                    No seat/date/deck changes specified
                  </Descriptions.Item>
                )}
                {r.computedRefund != null && (
                  <Descriptions.Item label="Refund to wallet">
                    <strong style={{ color: "#16a34a" }}>{money(r.computedRefund)}</strong>
                  </Descriptions.Item>
                )}
                {r.computedDifference != null && (
                  <Descriptions.Item label="Customer owes">
                    <strong style={{ color: "#d97706" }}>{money(r.computedDifference)}</strong>
                  </Descriptions.Item>
                )}
              </>
            )}

            {r.customerNote && (
              <Descriptions.Item label="Customer note">
                <em style={{ color: "#374151" }}>{r.customerNote}</em>
              </Descriptions.Item>
            )}

            {r.adminNote && (
              <Descriptions.Item label="Admin note">
                <em style={{ color: "#374151" }}>{r.adminNote}</em>
              </Descriptions.Item>
            )}

            {r.reviewedAt && (
              <Descriptions.Item label="Reviewed">
                {new Date(r.reviewedAt).toLocaleString("en-GB")}
              </Descriptions.Item>
            )}
          </Descriptions>
        </div>
      </div>
    </div>
  );
};

const RequestsList = () => {
  const [menu, setMenu] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [status, setStatus] = useState(undefined);
  const [type, setType] = useState(undefined);
  const [review, setReview] = useState({ open: false, id: null, action: null, row: null });
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const q = { page, limit };
      if (status) q.status = status;
      if (type) q.type = type;
      const res = await requestsApi.getAll(q);
      setRows(res.data || []);
      setTotal(res.total || 0);
    } catch {
      message.error("Failed to load requests");
    } finally {
      setLoading(false);
    }
  }, [page, limit, status, type]);

  useEffect(() => { load(); }, [load]);

  const openReview = (row, action) => {
    setReview({ open: true, id: row.id, action, row });
    setNote("");
  };

  const submitReview = async () => {
    try {
      setBusy(true);
      if (review.action === "approve") await requestsApi.approve(review.id, note);
      else await requestsApi.reject(review.id, note);
      message.success(`Request ${review.action === "approve" ? "approved" : "rejected"}`);
      setReview({ open: false, id: null, action: null, row: null });
      setNote("");
      load();
    } catch (e) {
      message.error(e.response?.data?.message || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      title: "Customer",
      render: (_, r) => customerOf(r),
    },
    {
      title: "Trip & Date",
      render: (_, r) => {
        const res = r.reservation || {};
        const tripName = res.tripDate?.trip?.name;
        const date = fmtDate(res.tripDate?.date);
        const deck = res.deck?.name;
        return (
          <div>
            {tripName && <div style={{ fontWeight: 600 }}>{tripName}</div>}
            <div style={{ fontSize: 12, color: "#6b7280" }}>{date}</div>
            {deck && <div style={{ fontSize: 12, color: "#9ca3af" }}>{deck}</div>}
          </div>
        );
      },
    },
    {
      title: "Type",
      dataIndex: "type",
      render: (t) => <Tag color={t === "cancel" ? "red" : "blue"}>{t}</Tag>,
    },
    {
      title: "Request",
      render: (_, r) => {
        if (r.type === "cancel") {
          return (
            <div style={{ fontSize: 13 }}>
              Cancel booking
              {r.computedRefund != null && (
                <div style={{ fontSize: 12, color: "#16a34a" }}>
                  Refund {money(r.computedRefund)}
                </div>
              )}
            </div>
          );
        }
        const lines = changeLines(r);
        return (
          <div style={{ fontSize: 12 }}>
            {lines.length ? (
              lines.map((l, i) => (
                <div key={i} style={{ color: "#374151" }}>• {l}</div>
              ))
            ) : (
              <span style={{ color: "#9ca3af" }}>—</span>
            )}
            {r.computedDifference != null && (
              <div style={{ color: "#d97706", marginTop: 2 }}>
                +{money(r.computedDifference)} due
              </div>
            )}
            {r.computedRefund != null && (
              <div style={{ color: "#16a34a", marginTop: 2 }}>
                -{money(r.computedRefund)} refund
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Note",
      render: (_, r) =>
        r.customerNote ? (
          <Tooltip title={r.customerNote}>
            <span
              style={{
                fontSize: 12, color: "#6b7280", maxWidth: 140,
                display: "block", overflow: "hidden",
                textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}
            >
              {r.customerNote}
            </span>
          </Tooltip>
        ) : (
          <span style={{ color: "#d1d5db" }}>—</span>
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (s) => <Tag color={statusColor[s] || "default"}>{s.replace("_", " ")}</Tag>,
    },
    {
      title: "Submitted",
      dataIndex: "createdAt",
      render: (v) => (
        <span style={{ fontSize: 12, color: "#6b7280" }}>
          {new Date(v).toLocaleString("en-GB")}
        </span>
      ),
    },
    {
      title: "Actions",
      render: (_, r) =>
        r.status === "pending" ? (
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              size="small"
              type="primary"
              onClick={() => openReview(r, "approve")}
            >
              Approve
            </Button>
            <Button
              size="small"
              danger
              onClick={() => openReview(r, "reject")}
            >
              Reject
            </Button>
          </div>
        ) : (
          <span style={{ color: "#9ca3af", fontSize: 12 }}>—</span>
        ),
    },
  ];

  // Summary shown at top of the review modal
  const reviewSummary = review.row ? (() => {
    const r = review.row;
    const res = r.reservation || {};
    const trip = res.tripDate?.trip?.name;
    const date = fmtDate(res.tripDate?.date);
    const deck = res.deck?.name;
    const lines = changeLines(r);
    return (
      <div
        style={{
          background: "#f9fafb", border: "1px solid #e5e7eb",
          borderRadius: 8, padding: "12px 14px", marginBottom: 16, fontSize: 13,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 6 }}>
          {customerOf(r)}
        </div>
        <div style={{ color: "#6b7280" }}>
          {trip && <span>{trip}</span>}
          {date && <span> · {date}</span>}
          {deck && <span> · {deck}</span>}
        </div>
        {r.type === "cancel" ? (
          <div style={{ marginTop: 6, color: "#dc2626" }}>
            Cancel booking · Refund {money(r.computedRefund)} to wallet
          </div>
        ) : (
          <div style={{ marginTop: 6, color: "#2563eb" }}>
            {lines.length ? lines.join(" · ") : "Change request"}
            {r.computedDifference != null && ` · Customer owes ${money(r.computedDifference)}`}
            {r.computedRefund != null && ` · Refund ${money(r.computedRefund)}`}
          </div>
        )}
      </div>
    );
  })() : null;

  return (
    <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
      <Header onMenuClick={() => setMenu(!menu)} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="page-header">
            <div className="content-page-header">
              <h5>Booking Requests</h5>
            </div>
          </div>

          <div className="card" style={{ borderRadius: 14 }}>
            <div className="card-body">
              <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
                <Select
                  placeholder="Status"
                  allowClear
                  style={{ width: 180 }}
                  value={status}
                  onChange={(v) => { setPage(1); setStatus(v); }}
                  options={[
                    { value: "pending", label: "Pending" },
                    { value: "awaiting_payment", label: "Awaiting payment" },
                    { value: "approved", label: "Approved" },
                    { value: "rejected", label: "Rejected" },
                    { value: "completed", label: "Completed" },
                  ]}
                />
                <Select
                  placeholder="Type"
                  allowClear
                  style={{ width: 150 }}
                  value={type}
                  onChange={(v) => { setPage(1); setType(v); }}
                  options={[
                    { value: "cancel", label: "Cancel" },
                    { value: "change", label: "Change" },
                  ]}
                />
              </div>

              <Table
                rowKey="id"
                loading={loading}
                columns={columns}
                dataSource={rows}
                expandable={{ expandedRowRender }}
                pagination={{
                  current: page,
                  pageSize: limit,
                  total,
                  showSizeChanger: true,
                  onChange: (p, ps) => { setPage(p); setLimit(ps); },
                }}
                scroll={{ x: true }}
              />
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={review.open}
        title={review.action === "approve" ? "Approve request" : "Reject request"}
        onOk={submitReview}
        onCancel={() => setReview({ open: false, id: null, action: null, row: null })}
        confirmLoading={busy}
        okText={review.action === "approve" ? "Approve" : "Reject"}
        okButtonProps={{ danger: review.action === "reject" }}
        width={520}
      >
        {reviewSummary}
        <p style={{ color: "#6b7280", marginBottom: 10, fontSize: 13 }}>
          {review.action === "approve"
            ? review.row?.type === "cancel"
              ? "Approving will cancel the booking and credit the full paid amount to the customer's wallet."
              : "Approving will apply the change. If the new total is higher the customer will be asked to pay the difference."
            : "The booking stays unchanged and the customer is notified of the rejection."}
        </p>
        <Input.TextArea
          rows={3}
          placeholder="Optional note to the customer"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default RequestsList;
